'use client';

/**
 * Overlays: dialog, sheet, dropdown menu, tooltip, popover.
 *
 * All built on Radix, which handles the parts that are easy to get wrong and
 * hard to notice: focus trapping, restoring focus on close, `aria-modal`,
 * escape handling, and inert background content.
 */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({
  className,
  children,
  title,
  description,
  size = 'md',
  onOpenAutoFocus,
}: {
  className?: string;
  children: React.ReactNode;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Where focus lands when the dialogue opens.
   *
   * Radix focuses the first tabbable element, which is the close button in the
   * header — correct for a form, wrong for a dialogue whose whole purpose is a
   * pad of choices. A caller that has one obvious first action passes this and
   * focuses it, so a keyboard user is on the thing they came for rather than on
   * the way out.
   */
  onOpenAutoFocus?: (event: Event) => void;
}) {
  const widths = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
  }[size];

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/35 backdrop-blur-[1px]" />
      <DialogPrimitive.Content
        onOpenAutoFocus={onOpenAutoFocus}
        className={cn(
          // Full-height sheet on a phone, centred dialog from `sm` up.
          'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-[var(--radius-card)] border border-border bg-surface shadow-[var(--shadow-raised)]',
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88dvh] sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[var(--radius-card)]',
          widths,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3.5 sm:px-5">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-base font-semibold text-ink">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-0.5 text-sm text-ink-muted">
                {description}
              </DialogPrimitive.Description>
            ) : (
              <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
            )}
          </div>
          <DialogPrimitive.Close className="-mr-1 -mt-1 flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-ink-muted hover:bg-surface-muted hover:text-ink">
            <X className="size-4" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-4 sm:px-5', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 sm:px-5',
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Sheet (edge drawer)
// ---------------------------------------------------------------------------

/**
 * A panel that slides in from the edge of the screen.
 *
 * Built on the same Radix dialog as `Dialog`, and for the same reason: a panel
 * that covers the page is a modal whether or not it is called one. The
 * navigation drawer was originally hand-rolled from a `fixed inset-0` div, which
 * looked right and behaved wrongly — `Tab` walked straight out of the open
 * drawer into the page behind it, the trigger never got focus back on close, the
 * background still scrolled, and nothing told a screen reader that the rest of
 * the page had gone away. None of that is visible on a screen, and none of it is
 * caught by an automated scanner, which is exactly why it survived.
 *
 * Radix supplies focus containment, focus restoration, `aria-modal`, inert
 * background content, escape handling and scroll locking. This wrapper adds the
 * geometry and requires a title, so a drawer can never be anonymous to a screen
 * reader.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export function SheetContent({
  className,
  children,
  title,
  description,
  side = 'left',
  /** Renders the title visually; otherwise it is there for assistive tech only. */
  header,
  onCloseAutoFocus,
}: {
  className?: string;
  children: React.ReactNode;
  title: string;
  description?: string;
  side?: 'left' | 'right';
  header?: React.ReactNode;
  /**
   * Where focus goes when the sheet closes.
   *
   * Radix returns focus to its own `Trigger`, which is the right default and no
   * use to a sheet that has more than one way in — the navigation drawer opens
   * from a button in the header *and* from "More" in the bottom bar. With no
   * `Trigger` there is no ref to return to, and focus was landing nowhere:
   * `document.activeElement` became `body`, so a keyboard user who pressed
   * Escape was silently returned to the top of the document instead of to the
   * control they had just used. A caller with several openers passes this and
   * restores the one that was actually pressed.
   */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/35" />
      <DialogPrimitive.Content
        onCloseAutoFocus={onCloseAutoFocus}
        className={cn(
          'fixed inset-y-0 z-50 flex w-[17rem] max-w-[85vw] flex-col bg-surface shadow-[var(--shadow-raised)]',
          side === 'left' ? 'left-0' : 'right-0',
          className,
        )}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-3">
          {header ? (
            <DialogPrimitive.Title asChild>{header}</DialogPrimitive.Title>
          ) : (
            <DialogPrimitive.Title className="text-base font-semibold text-ink">{title}</DialogPrimitive.Title>
          )}
          {description ? (
            <DialogPrimitive.Description className="sr-only">{description}</DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
          )}
          <DialogPrimitive.Close
            aria-label="Close menu"
            className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="size-5" aria-hidden="true" />
          </DialogPrimitive.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ---------------------------------------------------------------------------
// Dropdown menu
// ---------------------------------------------------------------------------

export const DropdownMenu = DropdownPrimitive.Root;
export const DropdownMenuTrigger = DropdownPrimitive.Trigger;

export function DropdownMenuContent({
  className,
  align = 'end',
  children,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 min-w-[11rem] overflow-hidden rounded-[var(--radius-control)] border border-border bg-surface p-1 shadow-[var(--shadow-raised)]',
          className,
        )}
        {...props}
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  className,
  destructive,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Item> & { destructive?: boolean }) {
  return (
    <DropdownPrimitive.Item
      className={cn(
        'flex min-h-9 cursor-pointer select-none items-center gap-2 rounded-[6px] px-2.5 text-sm text-ink outline-none data-[highlighted]:bg-surface-muted data-[disabled]:opacity-50',
        destructive && 'text-danger-ink data-[highlighted]:bg-danger-soft',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuLabel({ className, ...props }: React.ComponentProps<typeof DropdownPrimitive.Label>) {
  return (
    <DropdownPrimitive.Label
      className={cn('px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-subtle', className)}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownPrimitive.Separator>) {
  return <DropdownPrimitive.Separator className={cn('my-1 h-px bg-border', className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Popover & tooltip
// ---------------------------------------------------------------------------

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;

export function PopoverContent({ className, align = 'start', ...props }: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 rounded-[var(--radius-card)] border border-border bg-surface p-3 shadow-[var(--shadow-raised)]',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * A tooltip is never the only place information lives — it repeats or expands
 * on something already visible, because it is unreachable on touch.
 */
export function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={6}
          className="z-50 max-w-xs rounded-[var(--radius-control)] bg-ink px-2.5 py-1.5 text-xs text-white shadow-[var(--shadow-raised)]"
        >
          {label}
          <TooltipPrimitive.Arrow className="fill-[var(--color-ink)]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

/**
 * A confirmation dialog for an action that cannot be undone from the UI.
 * The confirm button is never the default focus, so a stray Enter cannot
 * trigger it.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  children?: React.ReactNode;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title} description={description} size="sm">
        {children ? <DialogBody>{children}</DialogBody> : null}
        <DialogFooter>
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex min-h-11 items-center rounded-[var(--radius-control)] border border-border-strong bg-surface px-4 text-sm font-medium text-ink hover:bg-surface-muted"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'inline-flex min-h-11 items-center rounded-[var(--radius-control)] px-4 text-sm font-medium text-white disabled:opacity-60',
              destructive ? 'bg-danger hover:brightness-95' : 'bg-brand hover:bg-brand-hover',
            )}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
